import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Button, DatePicker, Modal, Spin, Switch, Select } from 'antd';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { omit } from 'lodash';
import { PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';

import FormField, { FormFieldOverrideOnChange } from '../helpers/FormField';
import { Uploader } from '../helpers/Components';
import FormSection from '../helpers/FormSection';

import { CompanySelectDropDown, SiteSelectMultiDropdown } from '../helpers/drop-downs-for-enums';

import { useDispatch, useSelector } from 'react-redux';
import { addNewUser, getUsersList, UsersAction } from '../../../features/user.store';
import { getUserRole } from '../../../features/auth.store';

import eng from '../../../utils/eng';
import { ROLE_TYPES } from './Users';
import { ROLE } from '../../../utils/features';

const extractApiErrorMessages = (err) => {
  const data = err?.data || err?.details?.data;
  const messages = [];
  const detailMessages = [];

  const pushMessage = (value) => {
    if (!value) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    messages.push(normalized);
  };

  pushMessage(err?.message);
  pushMessage(err?.err);
  pushMessage(err?.details?.message);
  pushMessage(err?.details?.error);
  pushMessage(data?.message);
  pushMessage(data?.error);
  pushMessage(data?.detail);
  pushMessage(data?.title);

  if (Array.isArray(data?.details)) {
    data.details.forEach((item) => {
      const normalized = String(item || '').trim();
      if (normalized) detailMessages.push(normalized);
    });
  }

  const collectStringLeaves = (value) => {
    if (value === null || value === undefined) return;

    if (typeof value === 'string') {
      pushMessage(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => collectStringLeaves(item));
      return;
    }

    if (typeof value === 'object') {
      Object.values(value).forEach((entry) => collectStringLeaves(entry));
    }
  };

  const nestedCollections = [
    err?.details,
    data?.errors,
    data?.fieldErrors,
    data?.violations,
    data?.subErrors,
  ];

  nestedCollections.forEach((collection) => {
    if (Array.isArray(collection)) {
      collection.forEach((item) => {
        if (typeof item === 'string') {
          pushMessage(item);
          return;
        }

        pushMessage(item?.message);
        pushMessage(item?.defaultMessage);
        pushMessage(item?.error);
        pushMessage(item?.detail);
      });
      return;
    }

    if (collection && typeof collection === 'object') {
      Object.values(collection).forEach((value) => {
        if (Array.isArray(value)) {
          value.forEach((entry) => pushMessage(entry));
        } else {
          collectStringLeaves(value);
        }
      });
    }
  });

  collectStringLeaves(data);

  const uniqueDetailMessages = [...new Set(detailMessages)].filter(Boolean);
  if (uniqueDetailMessages.length) {
    return uniqueDetailMessages;
  }

  const genericMessages = new Set(['internal server error', 'request failed']);
  const uniqueMessages = [...new Set(messages)].filter(Boolean);
  const specificMessages = uniqueMessages.filter((m) => !genericMessages.has(String(m).toLowerCase()));

  if (specificMessages.length) {
    return specificMessages;
  }

  if (err?.status) {
    return [`${err?.message || eng.userAddError || 'Failed to add user'} (HTTP ${err.status})`];
  }

  return uniqueMessages.length ? uniqueMessages : [eng.userAddError || 'Failed to add user'];
};

const showApiErrors = (err) => {
  const allMessages = extractApiErrorMessages(err);
  allMessages.forEach((message, index) => {
    toast.error(message, { toastId: `user-error-${index}-${message}` });
  });
};

const UsersAdd = ({ roles = [] }) => {
  const [showAddUser, setShowAddUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const dispatch = useDispatch();
  const userRole = useSelector(getUserRole);
  const allUsers = useSelector(getUsersList);

  const formRef = useRef();
  const imageUploadRef = useRef();

  const filteredRoles = useMemo(() => {
    if (userRole === ROLE.SA) return roles;
    return roles.filter((r) => r?.type !== ROLE_TYPES.SUPER_ADMIN);
  }, [roles, userRole]);

  const roleOptions = useMemo(() => {
    return filteredRoles.map((r) => ({
      label: `${r.displayName || r.display_name || r.name}`,
      value: r.id,
      meta: r,
    }));
  }, [filteredRoles]);

  const getRoleById = (roleId) => filteredRoles.find((r) => String(r.id) === String(roleId));

  const generateCertificationNumber = () =>
    Math.floor(1000000000 + Math.random() * 9000000000).toString();

  const clearPreview = () => {
    if (previewImage && previewImage.startsWith('blob:')) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
  };

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const closeAddUserModal = () => {
    setShowAddUser(false);
    setUploadFileList([]);
    clearPreview();
  };

  const handleOK = () => {
    formRef.current?.submitForm();
  };

  const handleSave = async (values) => {
    setLoading(true);

    try {
      const normalizedEmail = String(values.email || '').trim().toLowerCase();
      const isDuplicateEmail = (allUsers || []).some(
        (user) => String(user?.email || '').trim().toLowerCase() === normalizedEmail,
      );

      if (normalizedEmail && isDuplicateEmail) {
        toast.error('A user with this email address already exists.');
        return;
      }

      const selectedRole = getRoleById(values.roleId);

      if (!selectedRole) {
        toast.error('Please select a role');
        return;
      }

      const image = uploadFileList?.[0]?.originFileObj || null;

      let formValue = {
        fullName: values.fullName,
        email: values.email,
        telephoneNumber: values.telephoneNumber,
        certificationNumber: generateCertificationNumber(),
        certificationExpiry: values.certificationExpiry?.valueOf?.() || moment().valueOf(),
        isLocked: !values.enabled,
        role: {
          id: values.roleId,
        },
      };

      if (selectedRole.type === ROLE_TYPES.TENANT) {
        formValue.tenantIdentifier = values.tenantIdentifier;
      }

      if (selectedRole.type === ROLE_TYPES.SITE) {
        formValue.tenantIdentifier = values.tenantIdentifier;
        formValue.assignedSiteIds = (values.siteIds || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id));
      }

      if (
        selectedRole.type === ROLE_TYPES.MINEARC ||
        selectedRole.type === ROLE_TYPES.SUPER_ADMIN
      ) {
        formValue.tenantIdentifier = null;
        formValue.assignedSiteIds = [];
      }

      if (!formValue.tenantIdentifier) {
        formValue = omit(formValue, ['tenantIdentifier']);
      }

      if (!formValue.assignedSiteIds || formValue.assignedSiteIds.length === 0) {
        formValue = omit(formValue, ['assignedSiteIds']);
      }

      await dispatch(addNewUser({ formValue, image })).unwrap();

      toast.success(eng.toast.addUserSuccessfully);
      formRef.current?.resetForm();
      setUploadFileList([]);
      clearPreview();
      setShowAddUser(false);
      dispatch(UsersAction.setPage(0));
    } catch (err) {
      console.error('Add user failed:', err);
      showApiErrors(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddUser(true)}>
        Add User
      </Button>

      <Modal
        title="Add User"
        visible={showAddUser}
        onOk={handleOK}
        onCancel={closeAddUserModal}
        width={1120}
        okButtonProps={{ disabled: loading }}
        cancelButtonProps={{ disabled: loading }}
        destroyOnClose
      >
        <FormSection>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={YupValidation(filteredRoles)}
            validateOnBlur
            onSubmit={handleSave}
          >
            {({ values, setFieldValue }) => {
              const selectedRole = getRoleById(values.roleId);
              const showCompany =
                selectedRole?.type === ROLE_TYPES.TENANT || selectedRole?.type === ROLE_TYPES.SITE;
              const showSites = selectedRole?.type === ROLE_TYPES.SITE;

              return (
                <Form>
                  <Spin spinning={loading}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                      <div>
                        <FormField name="fullName" labelText="User Name" placeholder="Input here" />
                        <FormField name="email" labelText="Email Address" placeholder="Input here" />
                        <FormField
                          name="telephoneNumber"
                          labelText="Telephone Number"
                          placeholder="+61"
                        />

                        <FormFieldOverrideOnChange
                          name="roleId"
                          labelText="Role Name"
                          placeholder="Select Role"
                          InputComp={(props) => (
                            <Select
                              {...props}
                              style={{ width: '100%' }}
                              options={roleOptions}
                              placeholder="Select Role"
                              onChange={(val) => {
                                props.onChange?.(val);
                                setFieldValue('tenantIdentifier', null);
                                setFieldValue('siteIds', []);
                                dispatch(UsersAction.setSelectedCompanyToTable(null));
                              }}
                            />
                          )}
                        />

                        <div style={{ marginTop: 14 }}>
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>Enabled</div>
                          <Switch
                            checked={values.enabled}
                            onChange={(checked) => setFieldValue('enabled', checked)}
                          />
                        </div>
                      </div>

                      <div>
                        <FormFieldOverrideOnChange
                          name="certificationExpiry"
                          labelText="Certification Expiry"
                          InputComp={(props) => <DatePicker {...props} className="w-100" />}
                        />

                        {showCompany && (
                          <FormField
                            name="tenantIdentifier"
                            labelText="Company"
                            placeholder="Select Company"
                            InputComp={CompanySelectDropDown}
                            extraOnChange={(val) => {
                              dispatch(UsersAction.setSelectedCompanyToTable(val));
                              setFieldValue('tenantIdentifier', val);
                              setFieldValue('siteIds', []);
                            }}
                          />
                        )}

                        {showSites && (
                          <FormFieldOverrideOnChange
                            allowClear
                            mode="multiple"
                            name="siteIds"
                            labelText="Sites"
                            placeholder="Select Site(s)"
                            company={values.tenantIdentifier}
                            InputComp={(props) => (
                              <SiteSelectMultiDropdown
                                {...props}
                                company={values.tenantIdentifier}
                                value={values.siteIds}
                                onChange={(val) => {
                                  const normalized = (val || [])
                                    .map((id) => Number(id))
                                    .filter((id) => Number.isFinite(id));
                                  props.onChange?.(normalized);
                                  setFieldValue('siteIds', normalized);
                                }}
                              />
                            )}
                          />
                        )}

                        <Uploader
                          ref={imageUploadRef}
                          className="w-200px h-200px"
                          fileList={uploadFileList}
                          onFileListChange={setUploadFileList}
                          onPreviewReady={(previewUrl) => {
                            if (previewImage && previewImage.startsWith('blob:')) {
                              URL.revokeObjectURL(previewImage);
                            }
                            setPreviewImage(previewUrl);
                          }}
                        />

                        {previewImage && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview</div>
                            <img
                              src={previewImage}
                              alt="Preview"
                              style={{
                                width: 200,
                                height: 200,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #d9d9d9',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Spin>
                </Form>
              );
            }}
          </Formik>
        </FormSection>
      </Modal>
    </>
  );
};

const initialValues = {
  fullName: '',
  email: '',
  telephoneNumber: '',
  certificationExpiry: null,
  enabled: true,
  roleId: null,
  tenantIdentifier: null,
  siteIds: [],
};

const YupValidation = (roles) =>
  Yup.object({
    fullName: Yup.string().required('Required'),
    email: Yup.string().required('Required').email('Invalid email address'),
    telephoneNumber: Yup.string().required('Required'),
    roleId: Yup.mixed().required('Required'),
    enabled: Yup.boolean(),
    certificationExpiry: Yup.mixed().required('Required'),

    tenantIdentifier: Yup.string().nullable().when('roleId', {
      is: (roleId) => {
        const role = roles.find((r) => String(r.id) === String(roleId));
        return role?.type === ROLE_TYPES.TENANT || role?.type === ROLE_TYPES.SITE;
      },
      then: (schema) => schema.required('Company is required'),
      otherwise: (schema) => schema.nullable(),
    }),

    siteIds: Yup.array().when('roleId', {
      is: (roleId) => {
        const role = roles.find((r) => String(r.id) === String(roleId));
        return role?.type === ROLE_TYPES.SITE;
      },
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),
  });

export default UsersAdd;